import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  useTheme,
  InputAdornment,
  TextField,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Badge,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Search as SearchIcon,
  Edit,
  Delete,
  Comment,
} from "@mui/icons-material";
import LoadingComponent from "../components/LoadingComponent";
import CommentsDialog from "../components/CommentsDialog";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getProduct,
  deleteProduct,
  getComments,
} from "../redux/slices/product/thunk";
import { getOrdersCounter } from "../redux/slices/order/thunk";

const ServiceCard = React.memo(
  ({
    service,
    t,
    theme,
    navigate,
    dispatch,
    user,
    deleteLoading,
    setDeleteLoading,
    onOpenComments,
    commentCount = 0, // number of comments, if available
  }) => {
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleDeleteConfirm = () => {
      setConfirmOpen(false);
      handleDelete();
    };

    const goToEditPage = () => {
      navigate(`/editProduct/${service._id}`);
    };

    const handleViewComments = () => {
      onOpenComments(service._id);
    };

    const handleDelete = async () => {
      setDeleteLoading((prev) => ({ ...prev, [service._id]: true }));
      try {
        const query = `familyId=${user?._id}`;
        await dispatch(deleteProduct({ id: service._id }));
        await dispatch(getProduct({ query }));
      } catch (error) {
        console.log("error", error);
      } finally {
        setDeleteLoading((prev) => ({ ...prev, [service._id]: false }));
      }
    };

    const isDeleting = !!deleteLoading[service._id];

    return (
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.3s, box-shadow 0.3s",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: theme.shadows[6],
          },
        }}
      >
        {/* Image area */}
        <Box sx={{ position: "relative", width: "100%", pt: "56.25%" }}>
          <Box
            component="img"
            src={service.image}
            alt={service.name}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.5s ease",
              "&:hover": { transform: "scale(1.05)" },
            }}
          />
          {service.discount > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "error.main",
                color: "white",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontWeight: "bold",
                fontSize: "0.8rem",
              }}
            >
              {`${service.discount}% OFF`}
            </Box>
          )}
        </Box>

        {/* Details */}
        <Box
          sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}
        >
          {/* Name */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {service.name}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              flexGrow: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {service.description}
          </Typography>

          {/* Price & discount */}
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: service.discount > 0 ? "error.main" : "text.primary",
                  fontWeight: 700,
                }}
              >
                {service.discount > 0
                  ? (
                      service.price -
                      (service.price * service.discount) / 100
                    ).toFixed(2)
                  : service.price.toFixed(2)}{" "}
                <span>{t("rs")}</span>
              </Typography>
              {service.discount > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: "line-through",
                    color: "text.secondary",
                  }}
                >
                  {service.price.toFixed(2)} <span>{t("rs")}</span>
                </Typography>
              )}
            </Box>
          </Box>

          {/* Quantity / stock line with comment icon on the right */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 1,
            }}
          >
            {service.stock_limit ? (
              <Typography variant="caption" color="text.secondary">
                {t("common.availableQty")}:{" "}
                <span>{service.count_in_stock}</span>
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t("common.unlimitedNote")}
              </Typography>
            )}

            {/* Comment icon with badge */}
            <IconButton
              size="small"
              onClick={handleViewComments}
              aria-label={t("common.viewComments")}
            >
              {commentCount > 0 ? (
                <Badge
                  badgeContent={commentCount}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "10px",
                      height: "16px",
                      minWidth: "16px",
                      padding: "0 4px",
                    },
                  }}
                >
                  <Comment fontSize="small" />
                </Badge>
              ) : (
                <Comment fontSize="small" />
              )}
            </IconButton>
          </Box>

          {/* Divider before actions (optional) */}
          <Box sx={{ mt: 2 }}>
            <Divider />
          </Box>

          {/* Actions: edit/delete only */}
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={goToEditPage}
              startIcon={<Edit />}
              sx={{
                "& .MuiButton-startIcon": {
                  marginInlineEnd: theme.spacing(1),
                },
              }}
            >
              {t("common.edit")}
            </Button>
            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={() => setConfirmOpen(true)}
              startIcon={
                isDeleting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Delete />
                )
              }
              disabled={isDeleting}
              sx={{
                "& .MuiButton-startIcon": {
                  marginInlineEnd: theme.spacing(1),
                },
              }}
            >
              {t("common.delete")}
            </Button>
          </Box>
        </Box>
<Dialog
  open={confirmOpen}
  onClose={() => setConfirmOpen(false)}
  fullWidth
  maxWidth="xs"
>
  <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Delete color="error" /> 
    <Typography variant="h6" fontWeight="bold">
      {t("common.confirmDeleteTitle")}
    </Typography>
  </DialogTitle>

  <DialogContent>
    <Typography variant="body2" color="text.secondary">
      {t("common.confirmDeleteMessage")}
    </Typography>
  </DialogContent>

  <DialogActions sx={{ px: 3, pb: 2 , display:"flex", gap:2}}>
    <Button
      onClick={() => setConfirmOpen(false)}
    >
      {t("common.cancel")}
    </Button>
    <Button
      onClick={handleDeleteConfirm}
      variant="contained"
      color="error"
    >
      {t("common.delete")}
    </Button>
  </DialogActions>
</Dialog>

      </Paper>
    );
  }
);

const FamilyHome = () => {
  const [deleteLoading, setDeleteLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isRtl = i18n.language === "ar";
  const dispatch = useDispatch();

  const user = JSON.parse(localStorage.getItem("user"));
  const { error, loading, products, comments } = useSelector(
    (state) => state.productSlice
  );

  const [dialogOpen, setDialogOpen] = useState({
    open: false,
    productId: null,
  });

  const handleOpenComments = (productId) => {
    setDialogOpen({ open: true, productId });
  };

  const handleCloseComments = () => {
    setDialogOpen({ open: false, productId: null });
  };

  // Load products on mount
  useEffect(() => {
    const query = `familyId=${user?._id}`;
    dispatch(getProduct({ query }));
    dispatch(
      getOrdersCounter({ id: user?._id, type: user?.type, status: "pending" })
    );
  }, [dispatch, user?._id]);

  // Optimized search handler
  const filterServices = useCallback(() => {
    setIsSearching(true);
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      setFilteredServices(Array.isArray(products) ? products : []);
      setIsSearching(false);
      return;
    }

    const source = Array.isArray(products) ? products : [];
    const filtered = source.filter((s) => {
      const name = s.name?.toLowerCase() || "";
      const desc = s.description?.toLowerCase() || "";
      return name.includes(term) || desc.includes(term);
    });

    setFilteredServices(filtered);
    setIsSearching(false);
  }, [searchTerm, products]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (searchTerm || products.length > 0) {
      setIsSearching(true);
      setSearchTimeout(
        setTimeout(() => {
          filterServices();
        }, 300) // 300ms debounce
      );
    }

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm, products, filterServices]);

  // State for filtered services
  const [filteredServices, setFilteredServices] = useState([]);

  // Initialize filtered services when products load
  useEffect(() => {
    if (products.length > 0 && !searchTerm) {
      setFilteredServices(products);
    }
  }, [products, searchTerm]);

  // Memoized service cards
  const serviceCards = useMemo(() => {
    return filteredServices?.map((service) => (
      <Grid item xs={12} sm={6} md={4} lg={4} key={service._id}>
        <ServiceCard
          service={service}
          t={t}
          theme={theme}
          navigate={navigate}
          dispatch={dispatch}
          user={user}
          deleteLoading={deleteLoading}
          setDeleteLoading={setDeleteLoading}
          commentCount={service?.comments?.length}
          comments={comments}
          onOpenComments={handleOpenComments}
        />
      </Grid>
    ));
  }, [filteredServices, t, theme, navigate, dispatch, user, deleteLoading]);

  if (user?.type !== "family") {
    return <Navigate to="/" replace />;
  }
  if (loading && !products.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <LoadingComponent />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box component="main">
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        {/* Title & Subtitle */}
        <Box
          sx={{
            textAlign: "center",
            mb: { xs: 4, md: 6 },
            direction: isRtl ? "rtl" : "ltr",
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
              fontWeight: 600,
              mb: 1.5,
              color: theme.palette.mode === "light" ? "#333" : "#fff",
            }}
          >
            {t("services.title")}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 700,
              mx: "auto",
              fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
              fontWeight: 400,
              px: { xs: 2, md: 0 },
              opacity: 0.8,
            }}
          >
            {t("services.subtitle")}
          </Typography>
        </Box>

        {/* Search Field */}
        <Box sx={{ mb: 4, maxWidth: 800, mx: "auto" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t("services.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching ? (
                    <CircularProgress size={24} />
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              ),
              sx: { borderRadius: 1, height: 56 },
            }}
          />
        </Box>

        {/* Grid of ServiceCards */}
        <Grid
          container
          spacing={{ xs: 2, sm: 3, md: 4 }}
          sx={{ direction: isRtl ? "rtl" : "ltr" }}
        >
          {filteredServices.length === 0 ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  py: 4,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" sx={{ color: "text.secondary" }}>
                  {searchTerm
                    ? t("services.noResults")
                    : t("services.noServices")}
                </Typography>
              </Box>
            </Grid>
          ) : (
            serviceCards
          )}
        </Grid>
      </Container>
      <CommentsDialog
        open={dialogOpen.open}
        onClose={handleCloseComments}
        productId={dialogOpen.productId}
        dispatch={dispatch}
      />
    </Box>
  );
};

export default FamilyHome;
